import PropTypes from 'prop-types'
import React from 'react'
import { DropTarget } from 'react-dnd'
import deepEqual from 'fast-deep-equal'
import { injectIntl, FormattedMessage } from 'react-intl'
import { SubmissionError } from 'redux-form'
import { CHECKLIST_ITEM_NAME_MAX_LENGTH } from 'const/limits'
import { PermissionLevels } from 'utils/permission'
import {
  Button,
  ButtonGroup
} from 'react-bootstrap'
import { convertKanBoFormatToContentState, convertEditorStateToKanBoFormat } from 'components/RichText/utils/convert'
import KanBoIcon from 'components/KanBoIcon'
import getCharCount from 'components/RichText/utils/getCharCount'
import trimRichText from 'components/RichText/utils/trim'
import RichTextContent from 'components/RichText/Content'
import BusyOverlay from 'components/BusyOverlay'
import KanBoPicker from 'components/KanBoPicker'
import {
  ToggleModal
} from 'components/Modal'
import { Menu, MenuItem } from 'components/Menu'
import userDragType from 'components/User/dragType'
import ElementItem from '../../ElementItem'
import TemplateField from 'components/CardTemplateField'
import classes from './ChecklistItem.module.scss'
import { fromJS } from 'immutable'
import {
  EditorState,
  Modifier,
  SelectionState
} from 'draft-js'
import { withBoardPermission } from 'dataProviders/Board'

const draggingStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: '#ccc',
  opacity: 0.8,
  zIndex: 2
}

const ModalActionTypes = {
  CONVERT_TO_CARD: 'CARD',
  CONVERT_TO_SUBCARD: 'SUBCARD',
}

export class ChecklistItem extends React.Component {
  static propTypes = {
    cardContext: PropTypes.object.isRequired,

    intl: PropTypes.object.isRequired,
    connectUserDropTarget: PropTypes.func.isRequired,
    isUserOver: PropTypes.bool.isRequired,
    canDropUser: PropTypes.bool.isRequired,

    permission: PropTypes.object.isRequired,

    isCardArchived: PropTypes.bool.isRequired,
    id: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string
    ]),
    checklistId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string
    ]),
    boardId: PropTypes.number.isRequired,
    statusId: PropTypes.number.isRequired,
    isTemplate: PropTypes.bool.isRequired,
    name: PropTypes.object.isRequired,
    completed: PropTypes.bool.isRequired,
    isBusy: PropTypes.bool,

    requestToggleChecklistItem: PropTypes.func.isRequired,
    requestChangeChecklistItemName: PropTypes.func.isRequired,
    requestRemoveChecklistItem: PropTypes.func.isRequired,
    requestConvertChecklistItemToCard: PropTypes.func.isRequired,
    requestConvertChecklistItemToSubCard: PropTypes.func.isRequired,

    sortable: PropTypes.object.isRequired
  }

  state = {
    isEditing: false,
    modalAction: null,
    convertBoardId: this.props.boardId,
    convertStatusId: null,
    convertUserId: null,
    convertUserName: null,
    convertTemplate: null,
  }

  handleIsEditingChange = (isEditing) => {
    this.setState({
      isEditing
    })
  }

  handleNameSubmit = (name) => {
    const { cardContext, intl, id, requestChangeChecklistItemName } = this.props

    return new Promise((resolve) => {
      const contentCharCount = getCharCount(name)
      if (contentCharCount === 0) {
        throw new SubmissionError({ content: intl.formatMessage({ id: 'CHECKLISTITEM_EMPTY_ERROR' }) })
      } else if (contentCharCount > CHECKLIST_ITEM_NAME_MAX_LENGTH) {
        throw new SubmissionError({
          content: intl.formatMessage({ id: 'CHECKLISTITEM_NAME_TOO_LONG' },
            { limit: CHECKLIST_ITEM_NAME_MAX_LENGTH })
        })
      }

      if (name) {
        const trimmedName = trimRichText(name)

        if (!deepEqual(trimmedName, this.props.name)) {
          requestChangeChecklistItemName({
            cardId: cardContext.id,
            checklistItemId: id,
            name: trimmedName
          })
        }
      }

      resolve()
    })
  }

  handleCheckboxClick = (event) => {
    event.preventDefault()

    const { cardContext, id, completed, requestToggleChecklistItem } = this.props

    requestToggleChecklistItem({
      cardId: cardContext.id,
      checklistItemId: id,
      completed: !completed
    })
  }

  hideModalToggle = () => {
    this.setState({
      modalAction: null,
      convertBoardId: this.props.boardId,
      convertStatusId: null,
      convertUserId: null,
      convertUserName: null,
      convertTemplate: null,
    })
  }

  handleConvertToCardClick = () => {
    this.setState({
      modalAction: ModalActionTypes.CONVERT_TO_CARD,
      convertBoardId: this.props.boardId,
      convertStatusId: this.props.statusId,
      convertUserId: null,
      convertUserName: null,
      convertTemplate: null,
    })
  }

  handleConvertToSubCardClick = () => {
    this.setState({
      modalAction: ModalActionTypes.CONVERT_TO_SUBCARD,
      convertBoardId: this.props.boardId,
      convertStatusId: this.props.statusId,
      convertUserId: null,
      convertUserName: null,
      convertTemplate: null,
    })
  }

  handleBoardChange = (convertBoardId) => {
    this.setState({
      convertBoardId,
      convertTemplate: null,
    })
  }

  handleStatusChange = (convertStatusId) => {
    this.setState({
      convertStatusId
    })
  }

  handleTemplateChange = (template) => {
    this.setState({
      convertTemplate: template
    })
  }

  handleConvertClick = () => {
    const { id, checklistId, requestConvertChecklistItemToCard, requestConvertChecklistItemToSubCard } = this.props
    const { convertStatusId, convertUserId, convertTemplate } = this.state

    switch (this.state.modalAction) {
      case ModalActionTypes.CONVERT_TO_CARD:
        requestConvertChecklistItemToCard({
          checklistItemId: id,
          checklistId,
          statusId: convertStatusId,
          userId: convertUserId,
          template: (convertTemplate && convertTemplate !== 'empty') ? { type: 'card', id: +convertTemplate } : 'empty',
        })
        break
      case ModalActionTypes.CONVERT_TO_SUBCARD:
        requestConvertChecklistItemToSubCard({
          checklistItemId: id,
          checklistId,
          statusId: convertStatusId,
          userId: convertUserId,
          template: (convertTemplate && convertTemplate !== 'empty') ? { type: 'card', id: +convertTemplate } : 'empty',
        })
        break
      default:
        break
    }
  }

  handleRemoveClick = () => {
    const { id, checklistId, requestRemoveChecklistItem } = this.props

    requestRemoveChecklistItem({
      checklistItemId: id,
      checklistId
    })
  }

  handleShowConvertModal = () => {
    this.setState({
      modalAction: ModalActionTypes.CONVERT_TO_SUBCARD
    })
  }

  mentionUser = () => {
    const { name } = this.props
    const { convertUserId, convertUserName } = this.state

    let editorState = EditorState.createWithContent(convertKanBoFormatToContentState(name))

    let contentState = editorState.getCurrentContent()

    const blockMap = contentState.getBlockMap()
    const key = blockMap.last().getKey()
    const length = blockMap.last().getLength()
    const selection = new SelectionState({
      anchorKey: key,
      anchorOffset: length,
      focusKey: key,
      focusOffset: length,
    })
    editorState = EditorState.acceptSelection(editorState, selection)

    contentState = Modifier.insertText(
      contentState,
      selection,
      ' '
    )

    const contentStateWithEntity = contentState.createEntity(
      'mention',
      'IMMUTABLE',
      {
        mention: fromJS({
          id: convertUserId,
          name: convertUserName
        })
      }
    )

    const entityKey = contentStateWithEntity.getLastCreatedEntityKey()

    const contentStateWithMention = Modifier.replaceText(
      contentStateWithEntity,
      contentState.getSelectionAfter(),
      convertUserName,
      null,
      entityKey
    )
    this.handleNameSubmit(convertEditorStateToKanBoFormat(EditorState.createWithContent(contentStateWithMention)))
    this.hideModalToggle()
  }

  renderItem = () => {
    const {
      isUserOver,

      permission,
      isCardArchived,
      boardId,

      id,
      name,
      completed,
      isBusy,
      isTemplate,

      sortable
    } = this.props
    const {
      isEditing
    } = this.state

    let menu

    if (!isCardArchived && permission.member) {
      menu = []

      menu.push({
        icon: <KanBoIcon fixedWidth name='kanbo-16-convert-to-card' />,
        title: <FormattedMessage id='CHECKLISTITEM_CONVERT_TO_CARD' />,
        onClick: this.handleConvertToCardClick
      })

      if (!isTemplate) {
        menu.push({
          icon: <KanBoIcon fixedWidth name='kanbo-16-relation-child' />,
          title: <FormattedMessage id='CHECKLISTITEM_CONVERT_TO_SUBCARD' />,
          onClick: this.handleConvertToSubCardClick
        })
      }

      menu.push({
        icon: <KanBoIcon fixedWidth name='kanbo-16-remove' />,
        title: <FormattedMessage id='REMOVE' />,
        onClick: this.handleRemoveClick
      })
    }

    let checkboxClassName = classes.checkbox
    if (!isCardArchived && permission.member) checkboxClassName += ' ' + classes.clickable
    if (completed) checkboxClassName += ` ${classes.completed}`

    return (
      <ElementItem
        hoverable={!isCardArchived && permission.member}
        icon={
          <span className={classes.checkboxWrapper}>
            <KanBoIcon
              name={completed ? 'kanbo-16-solid-check-box-checked' : 'kanbo-16-check-box-unchecked'}
              className={checkboxClassName}
              onClick={(!isCardArchived && permission.member) ? this.handleCheckboxClick : null}
            />
          </span>
        }
        menu={menu}
      >
        {(sortable.isDragging || isUserOver) && <div style={draggingStyle} />}
        {isBusy && <BusyOverlay />}
        <RichTextContent
          content={name}
          mentionBoardId={boardId}
          saveChanges={'checklistItemNameEdit-' + id}
          editable={!isCardArchived && permission.member}
          isEditing={isEditing}
          onIsEditingChange={this.handleIsEditingChange}
          onSubmit={this.handleNameSubmit}
        />
      </ElementItem>
    )
  }

  render () {
    const {
      connectUserDropTarget,
      canDropUser,

      boardId,
      statusId,
      isTemplate,
      isBusy,
      sortable
    } = this.props
    const {
      isEditing,
      modalAction,
      convertBoardId,
      convertStatusId,
      convertTemplate
    } = this.state

    const isSortable = !sortable.disabled && !isEditing && !isBusy

    return (
      <ToggleModal
        show={!!modalAction}
        backdrop
        attachClickHandler={false}
        attachment='top right'
        targetAttachment='top right'
        constraints={[
          {
            to: 'window',
            pin: true
          }
        ]}
        onToggle={this.hideModalToggle}
      >
        {(canDropUser ? connectUserDropTarget : (ret) => ret)(
          isSortable
            ? (
              sortable.connectDragSource(
                <div>{this.renderItem()}</div>
              )
            )
            : this.renderItem()
        )}
        {modalAction === true &&
          <Menu>
            {!isTemplate &&
              <MenuItem
                icon={<KanBoIcon fixedWidth name='kanbo-16-relation-child' />}
                onClick={this.handleShowConvertModal}
              >
                <FormattedMessage
                  id='CHECKLISTITEM_CONVERT_TO_SUBCARD'
                />
              </MenuItem>
            }
            <MenuItem
              icon={<KanBoIcon fixedWidth name='kanbo-16-user' />}
              onClick={this.mentionUser}
            >
              <FormattedMessage
                id='CHECKLISTITEM_MENTION_USER'
              />
            </MenuItem>
          </Menu>
        }
        {(modalAction === ModalActionTypes.CONVERT_TO_CARD || ModalActionTypes.CONVERT_TO_SUBCARD) &&
          <div className={classes.modal}>
            <KanBoPicker
              board
              status
              currentBoardId={boardId}
              currentStatusId={statusId}
              requiredPermissionLevel={PermissionLevels.MEMBER}
              onBoardChange={this.handleBoardChange}
              onStatusChange={this.handleStatusChange}
            />
            <TemplateField
              boardId={convertBoardId}
              value={convertTemplate}
              onChange={this.handleTemplateChange}
            />
            <ButtonGroup>
              <Button
                bsStyle='primary'
                disabled={!convertStatusId}
                onClick={this.handleConvertClick}
              >
                {modalAction === ModalActionTypes.CONVERT_TO_CARD &&
                  <FormattedMessage id='CHECKLISTITEM_CONVERT_TO_CARD' />
                }
                {modalAction === ModalActionTypes.CONVERT_TO_SUBCARD &&
                  <FormattedMessage id='CHECKLISTITEM_CONVERT_TO_SUBCARD' />
                }
              </Button>
              <Button
                onClick={this.hideModalToggle}
              >
                <FormattedMessage id='CANCEL' />
              </Button>
            </ButtonGroup>
          </div>
        }
      </ToggleModal>
    )
  }
}

const userDropTarget = {
  canDrop (props, monitor) {
    const { permission } = props

    return permission.member
  },

  drop (props, monitor, component) {
    const { statusId } = props
    const { id: userId, name: userName } = monitor.getItem()

    component.setState({
      modalAction: true,
      convertStatusId: statusId,
      convertUserId: userId,
      convertUserName: userName
    })
  }
}

export default
injectIntl(
  withBoardPermission(
    DropTarget(userDragType, userDropTarget, (connect, monitor) => ({
      connectUserDropTarget: connect.dropTarget(),
      isUserOver: monitor.isOver(),
      canDropUser: monitor.canDrop()
    }))(ChecklistItem)
  ),
  {
    forwardRef: true,
  }
)
